import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'
import dayjs from 'dayjs'

import { useCallMutation } from '@/common/api/client'
import { apiPostFetcher, apiPutFetcher } from '@/common/api/fetcher'
import {
  CreateLinkageApiKeyResponse,
  CreateLinkageApiKeyVars,
  UpdateLinkageApiKeyResponse,
  UpdateLinkageApiKeyVars
} from '@/common/api/params/linkageApiKey'
import { ApiPath } from '@/common/api/path'
import { useFormErrorMessages, useMessageStates } from '@/common/app/message'
import { linkageApiKeyEditPath } from '@/common/app/path'
import { useShowCopiedToast, useShowCopyFailedToast, useShowSavedToast } from '@/common/app/toast'
import { LinkageApiKey } from '@/common/model/linkageApiKey'
import { copyTextToClipboard } from '@/common/util/clipboard'
import { preventKeyDown } from '@/common/util/keyboard'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import FormCaption from '@/components/view/atoms/form/FormCaption'
import FormLabel from '@/components/view/atoms/form/FormLabel'
import Heading from '@/components/view/atoms/heading/Heading'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import { FieldType } from '@/components/view/atoms/textfield/TextField'
import { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import FormRow from '@/components/view/layouts/form/FormRow'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import PageBottom from '@/components/view/layouts/page/PageBottom'
import TopBar, { TopBarStatus } from '@/components/view/molecules/bar/TopBar'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import {
  DEFAULT_LINKAGE_API_KEY_SCHEMA,
  LinkageApiKeyFormSchemaType,
  convertLinkageApiKeySchemaToVars,
  convertLinkageApiKeyToSchema,
  linkageApiKeyNameLabelMapping,
  linkageApiKeySchema
} from '@/components/container/form/modules/admin/linkageApiKey/linkageApiKeySchema'
import FormError from '@/components/container/form/widgets/FormError'
import FormMessageList from '@/components/container/form/widgets/FormMessageList'
import FormTextField from '@/components/container/form/widgets/FormTextField'
import { useAppHeaderHiding } from '@/components/container/layout/AppHeaderProvider'

interface Props {
  linkageApiKey?: LinkageApiKey
  onRefetch?(): void
}

const LinkageApiKeyForm = ({ linkageApiKey, onRefetch }: Props) => {
  const navigate = useNavigate()
  const showCopied = useShowCopiedToast()
  const showCopyFailed = useShowCopyFailedToast()
  const {
    register,
    handleSubmit,
    control,
    getValues,
    formState: { errors, isDirty, isSubmitting },
    reset
  } = useForm<LinkageApiKeyFormSchemaType>({
    resolver: zodResolver(linkageApiKeySchema),
    values: linkageApiKey
      ? convertLinkageApiKeyToSchema(linkageApiKey)
      : DEFAULT_LINKAGE_API_KEY_SCHEMA,
    mode: 'onChange'
  })
  const {
    fields: ipFields,
    append: appendIp,
    remove: removeIp
  } = useFieldArray({ control, name: 'allowed_ips' })

  // 末尾の欄に入力されたら空欄を1つ足して、続けて複数IPを入力できるようにする。
  // 末尾を空に戻した場合は増やさない（送信時に空欄は除去される）。
  const onChangeIp = (idx: number) => {
    if (idx !== ipFields.length - 1) return
    if (getValues(`allowed_ips.${idx}.value`).trim() === '') return
    appendIp({ value: '' }, { shouldFocus: false })
  }

  useAppHeaderHiding(isDirty)
  const [messages, setMessages] = useMessageStates()
  useFormErrorMessages(errors, setMessages, linkageApiKeyNameLabelMapping)
  const showSaved = useShowSavedToast({ editing: !!linkageApiKey })

  const { trigger } = useCallMutation<
    CreateLinkageApiKeyResponse | UpdateLinkageApiKeyResponse,
    CreateLinkageApiKeyVars | UpdateLinkageApiKeyVars
  >(
    linkageApiKey ? ApiPath.UPDATE_LINKAGE_API_KEY : ApiPath.CREATE_LINKAGE_API_KEY,
    {
      onSuccess: (response) => {
        showSaved(response.purpose)
        if (linkageApiKey && onRefetch) {
          onRefetch()
        } else {
          navigate(linkageApiKeyEditPath(response.id))
        }
        reset(
          linkageApiKey
            ? convertLinkageApiKeyToSchema(linkageApiKey)
            : DEFAULT_LINKAGE_API_KEY_SCHEMA
        )
      }
    },
    linkageApiKey ? apiPutFetcher : apiPostFetcher
  )

  const onSubmit: SubmitHandler<LinkageApiKeyFormSchemaType> = (data) =>
    trigger(convertLinkageApiKeySchemaToVars(data))

  const onClickCopy = () => {
    if (!linkageApiKey) return
    copyTextToClipboard(linkageApiKey.api_key).then(
      () => showCopied('APIキー'),
      () => showCopyFailed('APIキー')
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyDown={preventKeyDown}>
      <PageTitle
        title={linkageApiKey ? linkageApiKey.purpose : '連携APIキーを新規作成する'}
        rightNode={
          <Button
            type='submit'
            size='large'
            color={ButtonColor.PRIMARY_BRAND}
            loading={isSubmitting}
          >
            {linkageApiKey ? '保存する' : '登録する'}
          </Button>
        }
      />
      {isDirty && (
        <TopBar
          status={linkageApiKey ? TopBarStatus.CHANGE : TopBarStatus.NEW}
          captionText={
            linkageApiKey ? '未反映の変更があります' : '登録されていない新しい連携APIキー'
          }
          onCancel={() =>
            reset(
              linkageApiKey
                ? convertLinkageApiKeyToSchema(linkageApiKey)
                : DEFAULT_LINKAGE_API_KEY_SCHEMA
            )
          }
          loading={isSubmitting}
        />
      )}
      <FormMessageList messageStates={messages} setMessageStates={setMessages} />
      <Content>
        <Area noBorder>
          <Heading>基本情報</Heading>
          <AreaContent>
            <FormRow>
              <FormLabel required id='purpose'>
                用途
              </FormLabel>
              <FormRow direction={'vertical'} gap={2}>
                <FormTextField
                  register={register('purpose')}
                  fieldType={FieldType.REQUIRED}
                  size={TextBoxSize.LARGE}
                  error={!!errors.purpose?.message}
                  placeholder='AIスタッフ連携'
                />
                <FormError message={errors.purpose?.message} />
                <FormCaption>連携先が分かる名称を入力してください</FormCaption>
              </FormRow>
            </FormRow>
            {!linkageApiKey && (
              <FormRow>
                <FormLabel id='api_key'>APIキー</FormLabel>
                <Text color={TextColor.SECONDARY}>
                  登録すると自動で発行されます。発行後、一覧または本画面から連携先へ共有してください。
                </Text>
              </FormRow>
            )}
          </AreaContent>
        </Area>
        <Area noBorder>
          <Heading>アクセス制限</Heading>
          <AreaContent>
            <FormRow>
              <FormLabel id='allowed_ips'>許可する送信元IP</FormLabel>
              <FormRow direction={'vertical'} gap={2} size={TextBoxSize.FULL}>
                {ipFields.map((field, idx) => (
                  <FormRow key={field.id} direction={'vertical'} gap={2} size={TextBoxSize.FULL}>
                    <ItemAlign gapX={8} align='center' full>
                      <FormTextField
                        register={register(`allowed_ips.${idx}.value`, {
                          onChange: () => onChangeIp(idx)
                        })}
                        error={!!errors.allowed_ips?.[idx]?.value?.message}
                        placeholder='203.0.113.10'
                      />
                      {idx < ipFields.length - 1 && (
                        <Button
                          color={ButtonColor.SECONDARY}
                          square
                          type='button'
                          onClick={() => removeIp(idx)}
                        >
                          <Icon type={IconType.DELETE} color={IconColor.CRITICAL} line />
                        </Button>
                      )}
                    </ItemAlign>
                    <FormError message={errors.allowed_ips?.[idx]?.value?.message} />
                  </FormRow>
                ))}
                <FormCaption>
                  入力したIPからのリクエストのみを許可します。空欄のままにすると制限しません（全IPを許可）。
                  <br />
                  CIDR（例:198.51.100.0/24）も指定できます。入力すると欄が下に追加されます。
                </FormCaption>
              </FormRow>
            </FormRow>
          </AreaContent>
        </Area>
        {linkageApiKey && (
          <Area noBorder>
            <Heading>APIキー</Heading>
            <AreaContent>
              <FormRow>
                <FormLabel id='api_key'>APIキー</FormLabel>
                <FormRow direction={'vertical'} gap={2}>
                  <ItemAlign gapX={8} align='center'>
                    <Text>{linkageApiKey.api_key}</Text>
                    <Button color={ButtonColor.SECONDARY} onClick={onClickCopy} type='button'>
                      コピー
                    </Button>
                  </ItemAlign>
                  <FormCaption>
                    リクエストの X-Api-Key ヘッダに設定する値です。変更はできません。
                  </FormCaption>
                </FormRow>
              </FormRow>
              <FormRow align='center'>
                <FormLabel id='created_at'>作成日時</FormLabel>
                <Text>
                  {linkageApiKey.created_at
                    ? dayjs(linkageApiKey.created_at).format('YYYY/MM/DD HH:mm')
                    : '-'}
                </Text>
              </FormRow>
              <FormRow align='center'>
                <FormLabel id='last_used_at'>最終利用日時</FormLabel>
                {linkageApiKey.last_used_at ? (
                  <Text>{dayjs(linkageApiKey.last_used_at).format('YYYY/MM/DD HH:mm')}</Text>
                ) : (
                  <Text color={TextColor.SECONDARY}>未使用</Text>
                )}
              </FormRow>
            </AreaContent>
          </Area>
        )}
      </Content>
      <PageBottom>
        <Button type='submit' size='large' color={ButtonColor.PRIMARY_BRAND} loading={isSubmitting}>
          {linkageApiKey ? '保存する' : '登録する'}
        </Button>
      </PageBottom>
    </form>
  )
}

export default LinkageApiKeyForm
